import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CreditCard,
  Bell,
  BookOpen,
  TrendingUp,
  User,
  Settings,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  Plus,
  Edit,
  Trash2,
  ExternalLink,
  Filter,
  Download,
  Home,
  LogOut,
  Dumbbell,
  Shield,
  CreditCard as CreditCardIcon,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProtectedRoute from '../components/ProtectedRoute';
import { useAdminStatus } from '../hooks/useAdminStatus';
import * as accountService from '../services/accountService';
import PlanDashboard from '../components/plans/PlanDashboard';
import PlanView from '../components/plans/PlanView';
import { canPayWithStripe, getOrCreateCheckoutUrl, redirectToCheckout } from '../services/stripeService';
import { isStripeConfigured } from '../lib/stripe';
import { formatRenewalDate, formatRenewalPeriod, isRenewalUpcoming } from '../utils/paymentUtils';

/**
 * Account Dashboard Page
 * Main user account portal with payment history, notices, programs, progress tracking, and profile settings
 */
export default function Account() {
  return (
    <ProtectedRoute>
      <AccountContent />
    </ProtectedRoute>
  );
}

function AccountContent() {
  const { user, profile, refreshProfile, updatePassword, signOut } = useAuth();
  const { isAdmin, refresh: refreshAdminStatus, isLoading: isAdminLoading, role } = useAdminStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const { planId } = useParams();
  
  // Check if we're viewing a plan detail page
  const isPlanDetailPage = location.pathname.includes('/programs/') && planId;

  // Debug logging for admin status
  useEffect(() => {
    if (import.meta.env.DEV && user) {
      console.log('[Account] Admin Status Check:', {
        userId: user.id,
        profileRole: profile?.role,
        hookRole: role,
        isAdmin,
        isAdminLoading,
        shouldShowAdminLink: isAdmin || profile?.role === 'admin' || role === 'admin',
      });
    }
  }, [user, profile?.role, isAdmin, isAdminLoading, role]);
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentsPagination, setPaymentsPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [paymentFilters, setPaymentFilters] = useState({}); // Filters for payment queries
  const [notices, setNotices] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [progressEntries, setProgressEntries] = useState([]);
  const [error, setError] = useState('');

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  // Load account data and refresh profile/admin status
  useEffect(() => {
    // Refresh profile and admin status when accessing account page
    if (user) {
      refreshProfile();
      refreshAdminStatus();
    }
    loadAccountData();

    // Check for Stripe payment redirect parameters
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');

    if (paymentStatus === 'success' && sessionId) {
      // Payment was successful, reload payments to show updated status
      // Wait a moment for webhook to process, then refresh
      setTimeout(() => {
        loadAccountData();
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 2000); // Increased delay to allow webhook to process
    } else if (paymentStatus === 'cancelled') {
      // Payment was cancelled
      setError('Payment was cancelled. You can try again anytime.');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Only depend on user, not the refresh functions

  const loadAccountData = async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    console.log('🔄 Loading account data for user:', user.id);
    setIsLoading(true);
    setError('');

    try {
      // Load data with individual timeouts and better error handling
      console.log('📊 Fetching account data...');
      
      const startTime = Date.now();
      
      const [summaryResult, paymentsResult, noticesResult, programsResult, progressResult] = await Promise.allSettled([
        accountService.getAccountSummary(user.id),
        accountService.getPayments(user.id, { 
          page: paymentsPagination.page, 
          pageSize: paymentsPagination.pageSize,
          ...paymentFilters,
        }),
        accountService.getNotices(user.id),
        accountService.getUserPrograms(user.id),
        accountService.getProgressEntries(user.id),
      ]);

      const elapsed = Date.now() - startTime;
      console.log(`⏱️ Account data loaded in ${elapsed}ms`);

      // Extract results from Promise.allSettled format
      const getResult = (settledResult) => {
        if (settledResult.status === 'fulfilled') {
          return settledResult.value;
        } else {
          console.error('Promise rejected:', settledResult.reason);
          return { success: false, error: settledResult.reason?.message || 'Unknown error', data: [] };
        }
      };

      const summary = getResult(summaryResult);
      const payments = getResult(paymentsResult);
      const notices = getResult(noticesResult);
      const programs = getResult(programsResult);
      const progress = getResult(progressResult);

      // Set data even if some requests failed (graceful degradation)
      if (summary?.success) {
        console.log('✅ Summary loaded:', summary.data);
        setSummary(summary.data);
      } else {
        console.warn('❌ Summary failed:', summary?.error);
      }

      if (payments?.success) {
        console.log('✅ Payments loaded:', payments.data?.length || 0, 'items');
        setPayments(payments.data || []);
        if (payments.pagination) {
          setPaymentsPagination(payments.pagination);
        }
      } else {
        console.warn('❌ Payments failed:', payments?.error);
        setPayments([]);
      }

      if (notices?.success) {
        console.log('✅ Notices loaded:', notices.data?.length || 0, 'items');
        setNotices(notices.data || []);
      } else {
        console.warn('❌ Notices failed:', notices?.error);
        setNotices([]);
      }

      if (programs?.success) {
        console.log('✅ Programs loaded:', programs.data?.length || 0, 'items');
        setPrograms(programs.data || []);
      } else {
        console.warn('❌ Programs failed:', programs?.error);
        setPrograms([]);
      }

      if (progress?.success) {
        console.log('✅ Progress loaded:', progress.data?.length || 0, 'items');
        setProgressEntries(progress.data || []);
      } else {
        console.warn('❌ Progress failed:', progress?.error);
        setProgressEntries([]);
      }

      // Check for errors and log them
      const errors = [summary, payments, notices, programs, progress]
        .filter(r => r && !r.success)
        .map(r => r.error);
      
      if (errors.length > 0) {
        console.warn('⚠️ Some account data failed to load:', errors);
        // Only show error if all critical requests failed
        if (!summary?.success && !payments?.success && !notices?.success) {
          setError('Failed to load account data. Please check your connection and try again.');
        }
      } else {
        console.log('✅ All account data loaded successfully');
      }
    } catch (err) {
      console.error('❌ Error loading account data:', err);
      setError(err.message || 'Failed to load account data. Please try again.');
      // Set empty arrays to prevent undefined errors
      setPayments([]);
      setNotices([]);
      setPrograms([]);
      setProgressEntries([]);
    } finally {
      console.log('🏁 Account data loading complete');
      setIsLoading(false);
    }
  };

  const handleMarkNoticeRead = async (noticeId) => {
    const result = await accountService.markNoticeRead(noticeId);
    if (result.success) {
      setNotices(notices.map(n => n.id === noticeId ? { ...n, is_read: true } : n));
    }
  };

  const handleMarkAllRead = async () => {
    if (!user) return;
    const result = await accountService.markAllNoticesRead(user.id);
    if (result.success) {
      setNotices(notices.map(n => ({ ...n, is_read: true })));
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'notices', label: 'Notices', icon: Bell },
    { id: 'programs', label: 'My Plans', icon: BookOpen },
    { id: 'progress', label: 'Progress', icon: TrendingUp },
    { id: 'profile', label: 'Profile', icon: User },
  ];

  // Auto-set programs tab if viewing a plan
  useEffect(() => {
    if (isPlanDetailPage && activeTab !== 'programs') {
      setActiveTab('programs');
    }
  }, [isPlanDetailPage]);


  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 pt-20">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your account...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-lg">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo/Home Link */}
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-600 to-accent-500 flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                <Dumbbell className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-xl font-display font-bold text-gray-900">
                  LC Performance
                </div>
                <div className="text-xs font-medium text-gray-600">
                  Personal Training
                </div>
              </div>
            </Link>

            {/* Navigation Links */}
            <nav className="hidden md:flex items-center gap-6">
              <Link
                to="/"
                className="text-gray-700 hover:text-primary-600 font-medium transition-colors flex items-center gap-2"
              >
                <Home className="w-4 h-4" />
                Home
              </Link>
              <Link
                to="/services"
                className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
              >
                Services
              </Link>
              <Link
                to="/contact"
                className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
              >
                Contact
              </Link>
              {/* Admin Dashboard Link - Check multiple sources for admin status */}
              {(isAdmin || profile?.role === 'admin' || role === 'admin') && (
                <Link
                  to="/admin"
                  className="text-red-600 hover:text-red-700 font-medium transition-colors flex items-center gap-2"
                  title="Admin Dashboard"
                >
                  <Shield className="w-4 h-4" />
                  Admin Dashboard
                </Link>
              )}
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-gray-900">
                  {profile?.full_name || user?.email?.split('@')[0]}
                </p>
                <p className="text-xs text-gray-500">
                  {(isAdmin || profile?.role === 'admin' || role === 'admin') ? 'Administrator' : 'Account'}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-primary-600 font-medium transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Account Header */}
      <section className="bg-gradient-to-br from-primary-700 via-primary-600 to-accent-500 text-white py-12 mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-2">
              My Account
            </h1>
            <p className="text-xl text-white/90">
              Welcome back, {profile?.full_name || user?.email}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tabs */}
      <section className="bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex overflow-x-auto space-x-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'text-primary-600 border-b-2 border-primary-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {activeTab === 'overview' && <OverviewTab summary={summary} />}
          {activeTab === 'payments' && (
            <PaymentsTab 
              payments={payments} 
              pagination={paymentsPagination}
              userId={user?.id}
              onPageChange={(page) => {
                setPaymentsPagination(prev => ({ ...prev, page }));
                // Reload payments with new page and current filters
                if (user?.id) {
                  accountService.getPayments(user.id, { 
                    page, 
                    pageSize: paymentsPagination.pageSize,
                    ...paymentFilters,
                  }).then(result => {
                    if (result.success) {
                      setPayments(result.data || []);
                      if (result.pagination) {
                        setPaymentsPagination(result.pagination);
                      }
                    }
                  });
                }
              }}
              onFilterChange={(filters) => {
                // Update filters and reload payments, reset to page 1
                setPaymentFilters(filters);
                setPaymentsPagination(prev => ({ ...prev, page: 1 }));
                if (user?.id) {
                  accountService.getPayments(user.id, { 
                    page: 1, 
                    pageSize: paymentsPagination.pageSize,
                    ...filters,
                  }).then(result => {
                    if (result.success) {
                      setPayments(result.data || []);
                      if (result.pagination) {
                        setPaymentsPagination(result.pagination);
                      }
                    }
                  });
                }
              }}
            />
          )}
          {activeTab === 'notices' && (
            <NoticesTab
              notices={notices}
              onMarkRead={handleMarkNoticeRead}
              onMarkAllRead={handleMarkAllRead}
            />
          )}
          {activeTab === 'programs' && (
            <>
              {isPlanDetailPage ? (
                <PlanView />
              ) : (
                <PlanDashboard />
              )}
            </>
          )}
          {activeTab === 'progress' && (
            <ProgressTab
              entries={progressEntries}
              userId={user?.id}
              onRefresh={loadAccountData}
            />
          )}
          {activeTab === 'profile' && (
            <ProfileTab
              user={user}
              profile={profile}
              onUpdate={refreshProfile}
              onPasswordUpdate={updatePassword}
            />
          )}
        </div>
      </section>
    </div>
  );
}

// Overview Tab Component
function OverviewTab({ summary }) {
  if (!summary) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">No data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Active Programs</h3>
            <BookOpen className="w-8 h-8 text-primary-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{summary.activePrograms}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Unread Notices</h3>
            <Bell className="w-8 h-8 text-accent-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{summary.unreadNotices}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Total Payments</h3>
            <CreditCard className="w-8 h-8 text-green-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{summary.totalPayments}</p>
        </motion.div>
      </div>

      {/* Recent Payments */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Payments</h3>
        {summary.recentPayments && summary.recentPayments.length > 0 ? (
          <div className="space-y-3">
            {summary.recentPayments.map((payment) => (
              <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">
                    ${payment.amount} - {payment.description || 'Payment'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {new Date(payment.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                    payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {payment.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600">No recent payments</p>
        )}
      </div>

      {/* Recent Notices */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Notices</h3>
        {summary.recentNotices && summary.recentNotices.length > 0 ? (
          <div className="space-y-3">
            {summary.recentNotices.map((notice) => (
              <div
                key={notice.id}
                className={`flex items-start gap-3 p-3 rounded-lg ${
                  !notice.is_read ? 'bg-primary-50 border border-primary-200' : 'bg-gray-50'
                }`}
              >
                <Bell className={`w-5 h-5 mt-0.5 ${
                  !notice.is_read ? 'text-primary-600' : 'text-gray-400'
                }`} />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{notice.title}</p>
                  <p className="text-sm text-gray-600">{notice.message}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(notice.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600">No recent notices</p>
        )}
      </div>
    </div>
  );
}

// Payments Tab Component
function PaymentsTab({ payments, pagination, onPageChange, onFilterChange, userId }) {
  const [filter, setFilter] = useState('new');
  const [processingPaymentId, setProcessingPaymentId] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
  });
  const [showDateFilter, setShowDateFilter] = useState(false);

  // Poll for payment status updates on pending payments
  useEffect(() => {
    const pendingPayments = payments.filter(p => 
      p.status === 'pending' && p.payment_provider === 'stripe'
    );

    if (pendingPayments.length === 0 || !userId) return;

    // Poll every 10 seconds for pending payments
    const pollInterval = setInterval(async () => {
      try {
        // Reload payments to check for status updates
        const result = await accountService.getPayments(userId, {
          page: pagination?.page || 1,
          pageSize: pagination?.pageSize || 10,
        });

        if (result.success && result.data) {
          // Check if any pending payments have changed status
          const updatedPayments = result.data;
          const hasStatusChange = pendingPayments.some(pendingPayment => {
            const updated = updatedPayments.find(p => p.id === pendingPayment.id);
            return updated && updated.status !== 'pending';
          });

          // If status changed, trigger a refresh
          if (hasStatusChange && onPageChange) {
            onPageChange(pagination?.page || 1);
          }
        }
      } catch (error) {
        console.error('Error polling payment status:', error);
      }
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(pollInterval);
  }, [payments, userId, pagination, onPageChange]);

  // Mark completed payments as viewed when "New" tab is active and payments are visible
  useEffect(() => {
    if (filter === 'new' && payments.length > 0 && userId) {
      const completedUnviewedPayments = payments.filter(
        p => p.status === 'completed' && !p.viewed_at
      );
      
      if (completedUnviewedPayments.length > 0) {
        // Mark as viewed after a short delay to ensure they're visible
        const timer = setTimeout(async () => {
          const paymentIds = completedUnviewedPayments.map(p => p.id);
          await accountService.markPaymentsAsViewed(userId, paymentIds);
          // Refresh to update viewed_at status
          if (onPageChange) {
            onPageChange(pagination?.page || 1);
          }
        }, 1000);
        
        return () => clearTimeout(timer);
      }
    }
  }, [filter, payments, userId, pagination, onPageChange]);

  const filteredPayments = payments.filter(payment => {
    if (filter === 'new') {
      // Show pending payments OR completed payments that haven't been viewed
      return payment.status === 'pending' || (payment.status === 'completed' && !payment.viewed_at);
    }
    if (filter === 'stripe') return payment.payment_provider === 'stripe';
    if (filter === 'affirm') return payment.payment_provider === 'affirm';
    return payment.status === filter;
  });

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const exportToCSV = () => {
    if (filteredPayments.length === 0) {
      alert('No payments to export');
      return;
    }

    // Create CSV headers
    const headers = ['Date', 'Description', 'Provider', 'Amount', 'Currency', 'Status', 'Type'];
    const rows = filteredPayments.map(payment => [
      new Date(payment.created_at).toLocaleDateString(),
      payment.description || 'Payment',
      payment.payment_provider,
      payment.amount,
      payment.currency || 'USD',
      payment.status,
      payment.payment_type,
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `payments_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePayNow = async (payment) => {
    if (!canPayWithStripe(payment)) {
      setPaymentError('This payment cannot be paid with Stripe.');
      setTimeout(() => setPaymentError(null), 5000);
      return;
    }

    if (!isStripeConfigured) {
      setPaymentError('Stripe is not configured. Please contact support.');
      setTimeout(() => setPaymentError(null), 5000);
      return;
    }

    setProcessingPaymentId(payment.id);
    setPaymentError(null);

    try {
      // Get or create checkout URL
      const checkoutUrl = await getOrCreateCheckoutUrl(payment, {
        successUrl: `${window.location.origin}/account?payment=success&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/account?payment=cancelled`,
      });

      // Redirect to Stripe Checkout
      await redirectToCheckout(checkoutUrl);
      // Note: User will be redirected, so we don't clear processingPaymentId here
    } catch (error) {
      console.error('Error initiating Stripe payment:', error);
      
      // Provide user-friendly error messages
      let errorMessage = 'Failed to initiate payment. ';
      const errorMsg = error.message || '';
      
      if (errorMsg.includes('Backend API') || errorMsg.includes('endpoint not available') || errorMsg.includes('Failed to fetch') || errorMsg.includes('NetworkError')) {
        errorMessage += 'Payment processing requires a backend server. The Stripe Checkout API endpoint is not available. Please contact support or ensure the backend server is running.';
      } else if (errorMsg.includes('not configured') || errorMsg.includes('Stripe is not configured')) {
        errorMessage += 'Payment system is not configured. Please contact support.';
      } else if (errorMsg.includes('network') || errorMsg.includes('fetch') || errorMsg.includes('Network')) {
        errorMessage += 'Network error. Please check your connection and try again.';
      } else if (errorMsg.includes('Failed to create checkout session')) {
        errorMessage += 'Unable to create payment session. This usually means the backend payment API is not available. Please contact support.';
      } else {
        errorMessage += errorMsg || 'Please try again or contact support if the problem persists.';
      }

      setPaymentError(errorMessage);
      setProcessingPaymentId(null);
      
      // Auto-dismiss error after 8 seconds
      setTimeout(() => setPaymentError(null), 8000);
    }
  };

  return (
    <div className="space-y-6">
      {/* Payment Error Alert */}
      <AnimatePresence>
        {paymentError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Payment Error</p>
              <p className="text-sm text-red-700 mt-1">{paymentError}</p>
              <button
                onClick={() => setPaymentError(null)}
                className="text-sm text-red-600 hover:text-red-800 mt-2 underline"
              >
                Dismiss
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payment Success Message */}
      <AnimatePresence>
        {new URLSearchParams(window.location.search).get('payment') === 'success' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3"
          >
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-800">Payment Successful!</p>
              <p className="text-sm text-green-700 mt-1">
                Your payment has been processed successfully. The payment status will update shortly.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4 flex-wrap">
            <Filter className="w-5 h-5 text-gray-600" />
          <button
            onClick={() => {
              setFilter('new');
              onFilterChange && onFilterChange({ status: null, provider: null });
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors relative ${
              filter === 'new'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            New
            {(() => {
              const newCount = payments.filter(p => 
                p.status === 'pending' || (p.status === 'completed' && !p.viewed_at)
              ).length;
              return newCount > 0 && filter !== 'new' ? (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {newCount}
                </span>
              ) : null;
            })()}
          </button>
          <button
            onClick={() => {
              setFilter('stripe');
              onFilterChange && onFilterChange({ provider: 'stripe', status: null });
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'stripe'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Stripe
          </button>
          <button
            onClick={() => {
              setFilter('affirm');
              onFilterChange && onFilterChange({ provider: 'affirm', status: null });
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'affirm'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Affirm
          </button>
          <button
            onClick={() => {
              setFilter('completed');
              onFilterChange && onFilterChange({ status: 'completed', provider: null });
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'completed'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Completed
          </button>
          <button
            onClick={() => {
              setFilter('pending');
              onFilterChange && onFilterChange({ status: 'pending', provider: null });
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'pending'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Pending
          </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowDateFilter(!showDateFilter)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              <Calendar className="w-4 h-4" />
              Date Range
            </button>
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors bg-primary-600 text-white hover:bg-primary-700"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Date Range Filter */}
        {showDateFilter && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => {
                    const newDateRange = { ...dateRange, startDate: e.target.value };
                    setDateRange(newDateRange);
                    // Apply date filter
                    if (onFilterChange) {
                      onFilterChange({
                        startDate: newDateRange.startDate || null,
                        endDate: newDateRange.endDate || null,
                      });
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => {
                    const newDateRange = { ...dateRange, endDate: e.target.value };
                    setDateRange(newDateRange);
                    // Apply date filter
                    if (onFilterChange) {
                      onFilterChange({
                        startDate: newDateRange.startDate || null,
                        endDate: newDateRange.endDate || null,
                      });
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            {(dateRange.startDate || dateRange.endDate) && (
              <button
                onClick={() => {
                  setDateRange({ startDate: '', endDate: '' });
                  if (onFilterChange) {
                    onFilterChange({
                      startDate: null,
                      endDate: null,
                    });
                  }
                }}
                className="mt-3 text-sm text-primary-600 hover:text-primary-800 underline"
              >
                Clear date filter
              </button>
            )}
          </div>
        )}
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Provider
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Renewal
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPayments.length > 0 ? (
                filteredPayments.map((payment) => {
                  const renewalUpcoming = payment.renewal_date && isRenewalUpcoming(payment.renewal_date, 7);
                  return (
                    <tr key={payment.id} className={`hover:bg-gray-50 ${renewalUpcoming ? 'bg-yellow-50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(payment.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {payment.description || 'Payment'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {payment.payment_provider}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(payment.amount, payment.currency)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                          payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          payment.status === 'failed' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {payment.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {payment.payment_type === 'subscription' ? (
                          <div className="flex flex-col gap-1">
                            {payment.renewal_period && (
                              <span className={`text-xs ${renewalUpcoming ? 'font-medium text-yellow-700' : ''}`}>
                                {formatRenewalPeriod(payment.renewal_period, payment.renewal_interval_months)}
                              </span>
                            )}
                            {payment.renewal_date && (
                              <span className={`text-xs ${renewalUpcoming ? 'font-medium text-yellow-700' : 'text-gray-500'}`}>
                                {payment.status === 'pending' ? 'Renews: ' : 'Next: '}
                                {formatRenewalDate(payment.renewal_date)}
                                {renewalUpcoming && (
                                  <span className="ml-1 text-yellow-600" title="Renewal within 7 days">
                                    ⚠️
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-2">
                          {canPayWithStripe(payment) && (
                            <button
                              onClick={() => handlePayNow(payment)}
                              disabled={processingPaymentId === payment.id}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                                processingPaymentId === payment.id
                                  ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                                  : 'bg-primary-600 text-white hover:bg-primary-700'
                              }`}
                            >
                              {processingPaymentId === payment.id ? (
                                <>
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <CreditCardIcon className="w-3 h-3" />
                                  Pay Now
                                </>
                              )}
                            </button>
                          )}
                          {payment.invoice_url && (
                            <a
                              href={payment.invoice_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-800 flex items-center gap-1"
                            >
                              Invoice <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    No payments found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {pagination && pagination.totalPages > 1 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.pageSize) + 1} to{' '}
              {Math.min(pagination.page * pagination.pageSize, pagination.total)} of{' '}
              {pagination.total} payments
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onPageChange && onPageChange(pagination.page - 1)}
                disabled={!pagination.hasPreviousPage}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  pagination.hasPreviousPage
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Previous
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => onPageChange && onPageChange(pageNum)}
                      className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                        pagination.page === pageNum
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => onPageChange && onPageChange(pagination.page + 1)}
                disabled={!pagination.hasNextPage}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  pagination.hasNextPage
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Notices Tab Component
function NoticesTab({ notices, onMarkRead, onMarkAllRead }) {
  const unreadCount = notices.filter(n => !n.is_read).length;

  return (
    <div className="space-y-6">
      {/* Header with Mark All Read */}
      {unreadCount > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6 flex items-center justify-between">
          <p className="text-gray-700">
            You have <span className="font-semibold text-primary-600">{unreadCount}</span> unread notices
          </p>
          <button
            onClick={onMarkAllRead}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
          >
            Mark All as Read
          </button>
        </div>
      )}

      {/* Notices List */}
      <div className="space-y-4">
        {notices.length > 0 ? (
          notices.map((notice) => (
            <motion.div
              key={notice.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-white rounded-xl shadow-lg p-6 ${
                !notice.is_read ? 'border-l-4 border-primary-600' : ''
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Bell className={`w-5 h-5 ${
                      !notice.is_read ? 'text-primary-600' : 'text-gray-400'
                    }`} />
                    <h3 className="text-lg font-semibold text-gray-900">{notice.title}</h3>
                    {!notice.is_read && (
                      <span className="px-2 py-1 bg-primary-100 text-primary-800 text-xs font-medium rounded-full">
                        New
                      </span>
                    )}
                  </div>
                  <p className="text-gray-700 mb-3">{notice.message}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>{new Date(notice.created_at).toLocaleDateString()}</span>
                    {notice.action_url && (
                      <a
                        href={notice.action_url}
                        className="text-primary-600 hover:text-primary-800 flex items-center gap-1"
                      >
                        View <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
                {!notice.is_read && (
                  <button
                    onClick={() => onMarkRead(notice.id)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                  >
                    Mark as Read
                  </button>
                )}
              </div>
            </motion.div>
          ))
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No notices available</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Programs Tab Component
function ProgramsTab({ programs }) {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {programs.length > 0 ? (
        programs.map((program) => (
          <motion.div
            key={program.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{program.program_name}</h3>
                <p className="text-gray-600">{program.program_type.replace('_', ' ')}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(program.status)}`}>
                {program.status}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-500">Start Date</p>
                <p className="font-medium text-gray-900">
                  {new Date(program.start_date).toLocaleDateString()}
                </p>
              </div>
              {program.end_date && (
                <div>
                  <p className="text-sm text-gray-500">End Date</p>
                  <p className="font-medium text-gray-900">
                    {new Date(program.end_date).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
            {program.coach_notes && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-1">Coach Notes:</p>
                <p className="text-sm text-gray-600">{program.coach_notes}</p>
              </div>
            )}
          </motion.div>
        ))
      ) : (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No programs found</p>
        </div>
      )}
    </div>
  );
}

// Progress Tab Component
function ProgressTab({ entries, userId, onRefresh }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    entryType: 'workout',
    entryDate: new Date().toISOString().split('T')[0],
    notes: '',
    data: {},
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) return;

    setIsSubmitting(true);
    const result = await accountService.addProgressEntry(userId, formData);
    setIsSubmitting(false);

    if (result.success) {
      setShowAddForm(false);
      setFormData({
        entryType: 'workout',
        entryDate: new Date().toISOString().split('T')[0],
        notes: '',
        data: {},
      });
      onRefresh();
    }
  };

  return (
    <div className="space-y-6">
      {/* Add Entry Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Progress Entry
        </button>
      </div>

      {/* Add Entry Form */}
      {showAddForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h3 className="text-xl font-bold text-gray-900 mb-4">Add Progress Entry</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Entry Type
              </label>
              <select
                value={formData.entryType}
                onChange={(e) => setFormData({ ...formData, entryType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="workout">Workout</option>
                <option value="measurement">Measurement</option>
                <option value="weight">Weight</option>
                <option value="photo">Photo</option>
                <option value="note">Note</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date
              </label>
              <input
                type="date"
                value={formData.entryDate}
                onChange={(e) => setFormData({ ...formData, entryDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter notes about this entry..."
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Adding...' : 'Add Entry'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Progress Entries */}
      <div className="space-y-4">
        {entries.length > 0 ? (
          entries.map((entry) => (
            <motion.div
              key={entry.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 capitalize">
                    {entry.entry_type.replace('_', ' ')}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {new Date(entry.entry_date).toLocaleDateString()}
                  </p>
                </div>
                <span className="px-3 py-1 bg-primary-100 text-primary-800 text-xs font-medium rounded-full capitalize">
                  {entry.entry_type}
                </span>
              </div>
              {entry.notes && (
                <p className="text-gray-700 mb-2">{entry.notes}</p>
              )}
              {entry.data && Object.keys(entry.data).length > 0 && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <pre className="text-xs text-gray-600">
                    {JSON.stringify(entry.data, null, 2)}
                  </pre>
                </div>
              )}
            </motion.div>
          ))
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No progress entries yet</p>
            <p className="text-sm text-gray-500 mt-2">Add your first entry to start tracking your progress</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Profile Tab Component
function ProfileTab({ user, profile, onUpdate, onPasswordUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    full_name: profile?.full_name || '',
    phone: profile?.phone || '',
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [message, setMessage] = useState('');

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    if (!user) return;

    setIsUpdating(true);
    setMessage('');

    const result = await accountService.updateProfile(user.id, formData);
    setIsUpdating(false);

    if (result.success) {
      setMessage('Profile updated successfully!');
      setIsEditing(false);
      onUpdate();
      setTimeout(() => setMessage(''), 3000);
    } else {
      setMessage('Failed to update profile. Please try again.');
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setMessage('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage('Password must be at least 6 characters long');
      return;
    }

    setIsUpdating(true);
    const result = await onPasswordUpdate(passwordData.newPassword);
    setIsUpdating(false);

    if (result.success) {
      setMessage('Password updated successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setTimeout(() => setMessage(''), 3000);
    } else {
      setMessage(result.error || 'Failed to update password. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile Information */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Profile Information</h3>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center gap-2"
            >
              <Edit className="w-4 h-4" />
              Edit
            </button>
          )}
        </div>

        {message && (
          <div className={`mb-4 p-3 rounded-lg ${
            message.includes('successfully') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            {message}
          </div>
        )}

        {isEditing ? (
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
              />
              <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isUpdating}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {isUpdating ? 'Updating...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setFormData({
                    full_name: profile?.full_name || '',
                    phone: profile?.phone || '',
                  });
                }}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500">Full Name</p>
              <p className="text-lg font-medium text-gray-900">
                {profile?.full_name || 'Not set'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="text-lg font-medium text-gray-900">{user?.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Phone</p>
              <p className="text-lg font-medium text-gray-900">
                {profile?.phone || 'Not set'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Change Password */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Change Password</h3>
        <form onSubmit={handlePasswordUpdate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <input
              type="password"
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm New Password
            </label>
            <input
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            />
          </div>
          <button
            type="submit"
            disabled={isUpdating}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {isUpdating ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
